import { Deferred } from "@use/deferred";
import { isNode } from "@use/is-node";
import { limitConcurrency } from "@use/limit-concurrency";

/**
 * This will contain a map of all images that have already been cached, so that
 * we don't attempt to load the same image multiple times.
 */
const cache = new Map<string, Deferred<HTMLImageElement>>();

/**
 * Specifies "time to live" for cached images in milliseconds; after this
 * timeout images are no longer considered cached, so they will be fetched from
 * the remote server again upon next request.
 *
 * This is implemented to circumvent issues with certain browsers clearing their
 * cache if images haven't been requested for a while (looking specifically at
 * you, Chrome!).
 */
const cacheTtl = 600000;

/**
 * Internal helper that'll actually fetch the image from the remote location by
 * creating a blank image tag.
 */
async function requestImage(url: string): Promise<HTMLImageElement> {
    return new Promise<HTMLImageElement>((resolve, reject) => {
        // Create an image object, that we'll use to load the image
        const image = new Image();

        image.style.cssText = `
            position: absolute;
            top: 0;
            left: 0;
            width: 1px;
            opacity: 0;
            pointer-events: none;
        `;

        // Subscribe to the relevant load events to either resolve or reject
        // the image
        image.onload = () => { resolve(image); document.body.removeChild(image); };
        image.onabort = image.onerror = () => { reject(); document.body.removeChild(image); };

        // Begin preloading the image!
        image.src = url;

        // Force certain browsers to properly preload the image and cace a
        // bitmap of it by mounting it into the DOM
        document.body.appendChild(image);
    });
}

/**
 * Make sure that the browser doesn't try to fetch more than 6 images at a time
 * (which is currently the sweetspot, where we'll get the most performance out
 * of the user's network connection).
 */
const rateLimitedRequestImage = limitConcurrency(requestImage, 6);

/**
 * Attempts to preload the given image, unless it's already been cached (in
 * which case nothing is being done).
 */
export async function preloadImage(url: string): Promise<HTMLImageElement | undefined> {
    // This is a no-op on node.js environments (they cannot preload anything for
    // the user)
    if (isNode()) {
        return;
    }

    // If the image hasn't been requested yet, then do so now
    if (!cache.has(url)) {
        const promise = new Deferred<HTMLImageElement>();

        // Request the image
        rateLimitedRequestImage(url).then(
            // Once the image has been fetched, make sure we remove it from the
            // cache after the specified time to live
            (img) => {
                promise.resolve(img);

                // Remove from cache after the designated time to live
                setTimeout(
                    () => {cache.delete(url); },
                    cacheTtl,
                );
            },

            // If an error occurs, then clear our internal cache state
            // immediately so we can try to fetch the image again immediately
            () => {
                promise.reject();
                cache.delete(url);
            },
        );

        // Cache the request for future reference
        cache.set(url, promise);
    }

    // Grab deferred object from cache
    const deferred = <Deferred<HTMLImageElement>> cache.get(url);

    // Return simple promise externally
    return new Promise<HTMLImageElement>((resolve, reject) => {
        deferred.then(
            (img) => { resolve(img); },
            () => { reject(); },
        );
    });
}

/**
 * Helper that'll instantly let the embedding application know if the image has
 * been preloaded yet.
 */
export function isImagePreloaded(url: string): boolean {
    const deferred = cache.get(url);

    // If no cache object exists, then the image is obviously not to be
    // considered preloaded
    if (deferred === undefined) {
        return false;
    }

    // ... Otherwise return current resolve status
    return deferred.isResolved;
}
